import * as d3 from 'd3';
import * as moment from 'moment';

/**
 * https://stackoverflow.com/questions/10420352/converting-file-size-in-bytes-to-human-readable-string
 **/
export function filesize (bytes, si)
{
    if (!bytes) {
        return null;
    }

    let thresh = si ? 1000 : 1024;

    if (Math.abs (bytes) < thresh) {
        return bytes + ' B';
    }

    let units = si
        ? [ 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ]
        : [ 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    let u = -1;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.abs (bytes) >= thresh && u < units.length - 1);

    return `${bytes.toFixed (1)} ${units[u]}`;
}

export function time (seconds)
{
    let mins = seconds / 60,
        secs = seconds % 60;

    mins = '' + Math.floor (mins);
    secs = '' + secs;

    return mins.padStart (2, '0') + ':' + secs.padStart (2, '0');
}

export function timestamp (seconds)
{
    return moment (seconds * 1000).format ('LL');
}

export function timeago (seconds)
{
    return moment (seconds * 1000).fromNow ();
}

export function datetime (seconds)
{
    return new Date (seconds * 1000).toISOString ();
}

export function date (seconds)
{
  return moment (seconds * 1000).format ('MMMM Do YYYY');
}

export function fulldate (seconds)
{
  return moment (seconds * 1000).format ('MMMM Do YYYY hh:mm:ss A');
}

export function interpolateColour (percentage, from, to, invert, alpha)
{
    let fromRgb, toRgb;

    from = from || colour ('red');
    to   = to   || colour ('green');

    alpha = alpha || 1;

    fromRgb = d3.rgb (invert ? to : from);
    fromRgb.opacity = alpha;

    toRgb = d3.rgb (invert ? from : to);
    toRgb.opacity = alpha;

    return d3.scaleLinear ()
        .domain ([ 0, 100 ])
        .interpolate (d3.interpolateHcl)
        .range ([ fromRgb, toRgb ]) (percentage);
}

export function colour (name)
{
    let rgb = getComputedStyle (document.documentElement)
        .getPropertyValue(`--${name}`)
        .trim ()
        .split (',')
        .map (d => +d);

    return '#' + rgb.map (toHex).join ('');
}

export function darkenColour (colour, factor)
{
    return d3.color (colour).darker (factor);
}

export function inArray (needle, haystack)
{
    return haystack.indexOf (needle) !== -1;
}

export function inArrayI (needle, haystack)
{
  for (let value of haystack) {
    if (strcasecmp (needle, value) === 0) {
      return true;
    }
  }

  return false;
}

export function inArraySI (needle, haystack)
{
  needle = needle.toLowerCase ();


  for (let value of haystack) {
    value = value.toLowerCase ();

    if (value.indexOf (needle) !== -1) {
      return true;
    }
  }

  return false;
}

export function ucfirst (str)
{
    if (!str.length) {
      return '';
    }

    return str
        .charAt (0)
        .toUpperCase () + str.slice (1);
}

export function ucwords (str)
{
    return str
        .split (' ')
        .map (ucfirst)
        .join (' ');
}

export function inlineCss (properties)
{
    let inline = '';

    for (let key in properties) {
        inline += `${key}: ${properties [key]};`;
    }

    return inline;
}

export function httpBuildQuery (base, qs)
{
    let url;

    url = base;

    for (let q in qs) {
        if (! (q in qs) || !qs [q]) {
            continue;
        }

        url += `&${q}=${('' + qs [q]).replace (/#/g, '%23')}`;
    }

    if (url.indexOf ('?') === -1 && url.indexOf ('&') !== -1) {
      let x = url.indexOf ('&');

      url = url.substring (0, x) + '?' + url.substring (x + 1);
    }

    return url;
}

export function ordinal (n)
{
    let suffixes = [
        'th', 'st', 'nd', 'rd', 'th',
        'th', 'th', 'th', 'th', 'th'
    ];

    if ((n % 100) >= 11 && (n % 100) <= 13) {
        return n + 'th';
    }

    return n + suffixes [n % 10];
}

export function isTransition (items, key, current)
{
    if (current === 0) {
      return true;
    }

    if (current >= items.length) {
        return false;
    }

    // if (current === items.length - 1) {
    //   return true;
    // }

    let previousItem = items [current - 1],
        currentItem  = items [current];

    if (   !previousItem
        || !currentItem
        || ! (key in currentItem)
        || ! (key in previousItem)) {
      return false;
    }

    return previousItem [key] !== currentItem [key];
}

export function toHex (x)
{
    return ('0' + (Number (x).toString (16)))
        .slice (-2)
        .toUpperCase ()
}

export function relative (key, current, haystack)
{
    if (resolve (key, current) === null) {
        return 0;
    }

    let max = resolve (key, current);
    let min = resolve (key, current);

    forEach (haystack, (item) => {
      let value = resolve (key, item);

      if (value === null) {
          return;
      }

      max = Math.max (value, max);
      min = Math.min (value, min);
    });

    if (max === 0) {
      return 0;
    }

    if (min === max) {
      return 100;
    }

    return Math.round (
      (resolve (key, current) - min) / (max - min) * 100
    );
}

export function resolve (key, object)
{
    let paths = key.split ('.');

    for (let path of paths) {
        if (!object || ! (path in object)) {
            return null;
        }

        object = object [path];
    }

    return object;
}

export function increment (object, key, n)
{
    n = n || 1;

    if (! (key in object)) {
      object [key] = 0;
    }

    object [key] += n;
}

export function maxKey (object)
{
    return Math.max (... Object.keys (object));
}

export function minKey (object)
{
    return Math.min (... Object.keys (object));
}

export function maxValue (object)
{
    return Math.max (... Object.values (object));
}

export function minValue (object)
{
    return Math.min (... Object.values (object));
}

export function maxKeyByValue (object)
{
    let target = maxValue (object);

    for (let key in object) {
      if (object [key] === target) {
        return key;
      }
    }

    return null;
}

export function eventPath (e)
{
    return e.path || (e.composedPath && e.composedPath ());
}

export function isRated (game)
{
    return game && game.flags & 0x02;
}

export function* range (start, end)
{
    for (let i = start; i <= end; i++) {
        yield i;
    }
}

export function forEach (collection, predicate)
{
    let results = [];
    let index   = 0;

    for (let key in collection) {
        results.push (
            predicate (collection [key], key, index++)
        );
    }

    return results;
}

export function rand (a, b)
{
  return Math.floor (Math.random () * b) + a;
}


// export function arrayGroup (object)
// {
//   let results = [];

//   for (let key in object) {
//     results.push ({
//       x: key,
//       y: object [key]
//     });
//   }

//   return results;
// }

export function arrayGroup (object, order)
{
  let results = [];

  for (let key in object) {
    let res = {};

    if (!order) {
      order = Object.keys (object [key]);
    }

    for (let ord of order) {
      if (! (ord in object [key])) {
        object [key] [ord] = 0;
      }

      res [ord] = object [key] [ord];
    }

    results.push ({
      x: key,
      y: res
    });
  }

  return results;
}

export function arrayColumn (array, column)
{
    let results = [];

    for (let object of array) {
        if (column in object) {
            results.push (object [column]);
        }
    }

    return results;
}

export function weekToDate (w, y)
{
    var simple = new Date (y, 0, 1 + (w - 1) * 7);
    var dow    = simple.getDay ();
    var start  = simple;

    if (dow <= 4) {
      start.setDate (simple.getDate () - simple.getDay () + 1);
    } else {
      start.setDate (simple.getDate () + 8 - simple.getDay ());
    }

    return start;
}

export function first (obj)
{
  return obj [firstKey (obj)];
}

export function firstKey (obj)
{
  let keys = Object.keys (obj);

  if (!keys.length) {
    return null;
  }

  return keys [0];
}

export function unpack (props, keys)
{
  if (!keys) {
    keys = Object.keys (props);
  }

  let res = {};

  for (let key of keys) {
    if (! (key in props)) {
      res [key] = null;
    } else {
      res [key] = props [key];
    }
  }

  return res;
}

export function selected (selectMenu)
{
  return selectMenu.options.selectedIndex > -1 ?
          selectMenu.options [selectMenu.options.selectedIndex].value :
          null;
}

export function valueset (items, key)
{
    let set = [];

    items.forEach (item => {
        let value = resolve (key, item);

        if (value && !set.includes (value)) {
            set.push (value);
        }
    });

    return set;
}

export function keyset (items, key)
{
  let set = [];

  items.forEach (item => {
    let value = resolve (key, item);

    if (!value) {
        return;
    }

    Object
      .keys (value)
      .forEach (k => {
        if (!set.includes (k)) {
            set.push (k);
        }
      });
  });

  return set;
}

export function flatten (items)
{
  return []
    .concat
    .apply ([], items);
}

export function isFull (obj)
{
  let keys = Object.values (obj);

  return !
    [
      null,
      undefined
    ]
      .map (v => keys.includes (v))
      .includes (true);
}

export function filterByKey (arr, key, matchOn)
{
  return arr.filter (
    s => {
      s = s.key;

      for (let k of Object.keys (s)) {
        if (
          matchOn instanceof Array &&
          !matchOn.includes (k)
        ) {
          continue;
        }

        if (
          k in key &&
          key [k] !== null &&
          key [k] !== s [k]
        ) {
          return false;
        }
      }

      return true;
    }
  );
}

export function strcasecmp (s1, s2)
{
  s1 = ('' + s1).toLowerCase ();
  s2 = ('' + s2).toLowerCase ();

  if (s1 > s2) {
    return 1;
  } else if (s1 === s2) {
    return 0;
  }

  return -1;
}

export function urlencode (u) {
  return u ? u.replace (/#/g, '%23') : u;
}

export function league (s)
{
  if (s <= 5) {
    return 'grandmaster';
  } else if (s <= 10) {
    return 'master';
  } else if (s <= 15) {
    return 'diamond';
  } else if (s <= 25) {
    return 'platinum';
  } else if (s <= 150) {
    return 'gold';
  } else if (s <= 300) {
    return 'silver';
  } else {
    return 'bronze';
  }
}